import {
  deleteMemberFromSpace,
  deleteInvitation,
  fetchSpaceInvitations,
  fetchSpaceMembers,
  inviteMemberToSpace,
  makeMemberAthlete,
} from "app/actions/space";
import { usePager } from "app/components/helpers/usePager";
import customPrompt from "app/components/inputs/SelectBox/helpers/customPrompt";
import SpaceMemberModel from "app/models/SpaceMemberModel";
import { useActions } from "app/utils/hooks";
import useDebounce from "app/utils/hooks/useDebounce";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { RootState } from "typedefs";
import { shallowEqual, useSelector } from "react-redux";
import {
  cancelSpaceInvitation,
  resendInvitation,
} from "app/actions/invitation";
import UserModel from "app/models/UserModel";

// Pulls the first human readable message out of an API error body. The body
// nests differently per endpoint — a string, a list, or a map of field to list,
// sometimes a map of maps — so walk it rather than naming keys.
const firstErrorMessage = (errors: any): string | null => {
  if (typeof errors === "string") {
    return errors.trim() || null;
  }

  if (Array.isArray(errors)) {
    for (const entry of errors) {
      const message = firstErrorMessage(entry);
      if (message) {
        return message;
      }
    }
    return null;
  }

  if (errors && typeof errors === "object") {
    for (const value of Object.values(errors)) {
      const message = firstErrorMessage(value);
      if (message) {
        return message;
      }
    }
  }

  return null;
};

export const SpaceMembersModel = () => {
  const { t } = useTranslation();
  const { self } = useSelector((state: RootState) => state.user, shallowEqual);
  const spaceActions = useActions({
    makeMemberAthlete,
    cancelSpaceInvitation,
    deleteMemberFromSpace,
    deleteInvitation,
    inviteMemberToSpace,
    resendInvitation,
  });
  const invitationActions = useActions({
    fetchSpaceMembers,
    fetchSpaceInvitations,
  });
  const [search, setSearch] = useState(null);
  const [query] = useState<{ [key: string]: any }>({
    perPage: 20,
  });
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [inviteModalIsOpen, setInviteModalIsOpen] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [isRefreshingLists, setIsRefreshingLists] = useState(false);

  const spaceParams = useParams<{ id: string }>();

  const debouncedSearch = useDebounce(search ?? "", 300);

  const memberSearchHandler = (val: string) => {
    setSearch(val);
  };

  const {
    data: members,
    loading,
    sentryRef: membersRef,
    initialLoad: fetchMembers,
  } = usePager({
    api: (_id, params: any) => invitationActions.fetchSpaceMembers(_id, params),
    fetchId: spaceParams.id,
    query:
      search?.length >= 2 ? { ...query, userName: debouncedSearch } : query,
    currentPage: 1,
    totalPages: query.perPage,
  });

  const {
    data: invitedMembers,
    sentryRef: invitedMembersRef,
    initialLoad: fetchInvitedMembers,
  } = usePager({
    api: (_id, params: any) =>
      invitationActions.fetchSpaceInvitations(_id, params),
    fetchId: spaceParams.id,
    query: { ...query, options: { include: ["guardians"] } },
    currentPage: 1,
    totalPages: query.perPage,
  });

  const history = useHistory();

  const route = (path: string) => {
    history.push(path);
  };

  useEffect(() => {
    if (search?.length >= 2 || search?.length === 0) {
      fetchMembers();
    }
  }, [debouncedSearch]);

  const handleInviteModal = () => {
    setInviteModalIsOpen(!inviteModalIsOpen);
  };

  const onMakeAthlete = (memberId: string, member: SpaceMemberModel) => {
    customPrompt({
      message: t(
        `Changing athlete changes the current Space owner and athlete. Please confirm your action.`,
      ),
      onYes: () => {
        spaceActions
          .makeMemberAthlete(spaceParams.id, memberId, {
            ...member,
            role: "athlete",
          })
          .then(() => {
            fetchMembers();
          });
      },
      onCancel: () => {
        return;
      },
    });
  };

  const onRemoveUser = (member: UserModel) => {
    customPrompt({
      message: t(`Are you sure?`),
      onYes: () => {
        if (member?.object === "space_invitation") {
          spaceActions
            .cancelSpaceInvitation(spaceParams.id, member?.id)
            .then(() => {
              fetchInvitedMembers();
            });
        } else {
          spaceActions
            .deleteMemberFromSpace(spaceParams.id, member?.id)
            .then(() => {
              fetchMembers();
              fetchInvitedMembers();
              if (self && self?.id === member?.id) {
                route("/spaces");
              }
            });
        }
      },
      onCancel: () => {
        return;
      },
    });
  };

  const onMemberInvite = (invitation: { [key: string]: any }) => {
    setInvitationError(null);
    return spaceActions
      .inviteMemberToSpace(spaceParams.id, {
        ...invitation,
        spaceId: spaceParams.id,
      })
      .then((invitation: { [key: string]: any }) => {
        if (invitation.phone) {
          setInviteUrl(invitation.shortInvitationUrl);
          handleInviteModal();
        }
        // The invitee can land in either list (existing users may be added
        // straight to Members), so reload both behind a single spinner.
        setIsRefreshingLists(true);
        Promise.all([fetchMembers(), fetchInvitedMembers()]).finally(() =>
          setIsRefreshingLists(false),
        );
        toast.success(t("Invitation sent"));
        return true;
      })
      .catch((error: any) => {
        // CN-2834: this used to read two known keys and, for anything else,
        // claim the member was already invited. That sentence was shown for
        // every other failure, so a coach read the wrong cause and the real one
        // never reached us. Report whatever the API said instead.
        const message =
          firstErrorMessage(error?.message) ??
          firstErrorMessage(error?.originalErrors) ??
          t("Could not invite this member");
        setInvitationError(message);
        toast.error(t("Invitation could not be sent"));
        return false;
      });
  };

  const onCopyLink = (member: SpaceMemberModel) => {
    setInviteUrl(member.shortInvitationUrl);
    handleInviteModal();
  };

  const onEdit = (memberId: string) => {
    route(`/spaces/${spaceParams.id}/invitations/${memberId}/edit`);
  };

  const onResend = (invitation: UserModel) => {
    if (invitation) {
      spaceActions
        .resendInvitation(invitation)
        .then(() => {
          fetchInvitedMembers();
          toast.success(t("Invitation resent"));
        })
        .catch(() => {
          toast.error(
            t("Cannot resend this invitation, please try again later."),
          );
        });
    }
  };

  return {
    members,
    loading,
    membersRef,
    invitedMembers,
    invitedMembersRef,
    memberSearchHandler,
    onMakeAthlete,
    onRemoveUser,
    onMemberInvite,
    onCopyLink,
    onEdit,
    onResend,
    invitationError,
    isRefreshingLists,
    handleInviteModal,
    inviteModalIsOpen,
    inviteUrl,
  };
};
